import { Component } from "@angular/core";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
  ToastController,
} from "ionic-angular";
import { EcoEnelEmpresasProvider } from "../../../providers/ecoenel-empresas/ecoenel-empresas.provider";

/**
 * Pagina para Reciclador aprovar ou recusar uma reciclagem pendente
 * Suporta validacao individual de residuos e aprovacao/recusa global
 * Nota: Nao usa lazy loading - declarada no modulo pai
 */
@Component({
  selector: "page-reciclagem-responder",
  templateUrl: "reciclagem-responder.html",
})
export class ReciclagemResponderPage {
  lancamentoId: number;
  lancamento: any = null;
  motivosRecusa: any[] = [];
  loading: boolean = true;

  // Controle de fluxo
  mostrarFormRecusa: boolean = false;
  modoValidacaoIndividual: boolean = false;

  // Para recusa global
  motivoSelecionado: any = null;
  observacaoRecusa: string = "";

  // Para recusa individual de residuo
  residuoRecusando: any = null;
  motivoResiduo: any = null;
  comentarioResiduo: string = "";

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public provider: EcoEnelEmpresasProvider,
    public alertCtrl: AlertController,
    public loadCtrl: LoadingController,
    public toastCtrl: ToastController
  ) {
    this.lancamentoId = this.navParams.get("id");
  }

  ionViewDidEnter() {
    this.carregarDados();
  }

  carregarDados() {
    this.loading = true;

    // Carregar detalhes do lancamento
    this.provider.getLancamento(this.lancamentoId).subscribe(
      (data) => {
        this.lancamento = data;
        // Se lancamento ja esta em validacao (V), ativar modo individual automaticamente
        if (this.lancamento && this.lancamento.status && this.lancamento.status.codigo === "V") {
          this.modoValidacaoIndividual = true;
        }
        this.loading = false;
      },
      (err) => {
        this.showToast("Erro ao carregar detalhes");
        this.loading = false;
      }
    );

    // Carregar motivos de recusa
    this.provider.getMotivosRecusa().subscribe(
      (data) => {
        this.motivosRecusa = data || [];
      },
      (err) => {
        console.error("Erro ao carregar motivos", err);
      }
    );
  }

  // =========================================
  // STATUS HELPERS
  // =========================================

  getStatusResiduo(residuo: any): string {
    if (!residuo || !residuo.status || residuo.status === "P") return "pendente";
    if (residuo.status === "A") return "aprovado";
    if (residuo.status === "R") return "recusado";
    return "pendente";
  }

  getStatusResiduoLabel(residuo: any): string {
    var status = this.getStatusResiduo(residuo);
    if (status === "aprovado") return "Aprovado";
    if (status === "recusado") return "Recusado";
    return "Pendente";
  }

  getStatusResiduoColor(residuo: any): string {
    var status = this.getStatusResiduo(residuo);
    if (status === "aprovado") return "success";
    if (status === "recusado") return "danger";
    return "light";
  }

  isResidupPendente(residuo: any): boolean {
    return !residuo.status || residuo.status === "P";
  }

  getContadorValidados(): any {
    if (!this.lancamento || !this.lancamento.residuos) {
      return { total: 0, validados: 0, aprovados: 0, recusados: 0, pendentes: 0 };
    }
    var residuos = this.lancamento.residuos;
    var total = residuos.length;
    var aprovados = 0;
    var recusados = 0;
    for (var i = 0; i < residuos.length; i++) {
      if (residuos[i].status === "A") aprovados++;
      else if (residuos[i].status === "R") recusados++;
    }
    return {
      total: total,
      validados: aprovados + recusados,
      aprovados: aprovados,
      recusados: recusados,
      pendentes: total - aprovados - recusados,
    };
  }

  todosValidados(): boolean {
    var c = this.getContadorValidados();
    return c.total > 0 && c.validados === c.total;
  }

  podeValidarResiduos(): boolean {
    if (!this.lancamento || !this.lancamento.status) return false;
    var codigo = this.lancamento.status.codigo;
    return codigo === "S" || codigo === "V";
  }

  podeFinalizarValidacao(): boolean {
    if (!this.lancamento || !this.lancamento.status) return false;
    return this.lancamento.status.codigo === "V";
  }

  // =========================================
  // MODO DE VALIDACAO
  // =========================================

  ativarValidacaoIndividual() {
    this.modoValidacaoIndividual = true;
    this.mostrarFormRecusa = false;
  }

  voltarModoGlobal() {
    this.modoValidacaoIndividual = false;
    this.residuoRecusando = null;
  }

  // =========================================
  // APROVACAO/RECUSA GLOBAL (fluxo original)
  // =========================================

  aprovar() {
    this.alertCtrl
      .create({
        title: "Confirmar Aprovacao",
        message:
          "Deseja aprovar esta reciclagem? A reciclagem sera registrada no sistema.",
        buttons: [
          { text: "Cancelar", role: "cancel" },
          {
            text: "Aprovar",
            handler: () => {
              this.executarAprovacao();
            },
          },
        ],
      })
      .present();
  }

  executarAprovacao() {
    var load = this.loadCtrl.create({ content: "Aprovando..." });
    load.present();

    this.provider
      .aprovarLancamento(this.lancamentoId)
      .then((data) => {
        load.dismiss();
        this.showToast("Reciclagem aprovada com sucesso!");
        this.navCtrl.pop();
      })
      .catch((err) => {
        load.dismiss();
        this.showAlert(err.msg || "Erro ao aprovar");
      });
  }

  iniciarRecusa() {
    this.mostrarFormRecusa = true;
    this.motivoSelecionado = null;
    this.observacaoRecusa = "";
  }

  cancelarRecusa() {
    this.mostrarFormRecusa = false;
    this.motivoSelecionado = null;
    this.observacaoRecusa = "";
  }

  recusar() {
    if (!this.motivoSelecionado) {
      return this.showAlert("Selecione um motivo de recusa");
    }

    if (this.motivoSelecionado.requer_comentario && !this.observacaoRecusa.trim()) {
      return this.showAlert("Este motivo requer uma observacao obrigatoria");
    }

    this.alertCtrl
      .create({
        title: "Confirmar Recusa",
        message: "Deseja recusar esta reciclagem?",
        buttons: [
          { text: "Cancelar", role: "cancel" },
          {
            text: "Recusar",
            handler: () => {
              this.executarRecusa();
            },
          },
        ],
      })
      .present();
  }

  executarRecusa() {
    var load = this.loadCtrl.create({ content: "Recusando..." });
    load.present();

    var motivoCodigo = this.motivoSelecionado ? this.motivoSelecionado.codigo : "";

    this.provider
      .recusarLancamento(
        this.lancamentoId,
        motivoCodigo,
        this.observacaoRecusa
      )
      .then((data) => {
        load.dismiss();
        this.showToast("Reciclagem recusada");
        this.navCtrl.pop();
      })
      .catch((err) => {
        load.dismiss();
        this.showAlert(err.msg || "Erro ao recusar");
      });
  }

  // =========================================
  // VALIDACAO INDIVIDUAL DE RESIDUOS
  // =========================================

  aprovarResiduo(residuo: any) {
    this.alertCtrl
      .create({
        title: "Aprovar Residuo",
        message: "Deseja aprovar este residuo?",
        buttons: [
          { text: "Cancelar", role: "cancel" },
          {
            text: "Aprovar",
            handler: () => {
              this.executarAprovarResiduo(residuo);
            },
          },
        ],
      })
      .present();
  }

  executarAprovarResiduo(residuo: any) {
    var load = this.loadCtrl.create({ content: "Aprovando residuo..." });
    load.present();

    this.provider
      .aprovarResiduo(residuo.id)
      .then((data) => {
        load.dismiss();
        this.showToast("Residuo aprovado");
        this.lancamento = data.data;
        this.verificarFinalizacao();
      })
      .catch((err) => {
        load.dismiss();
        this.showAlert(err.msg || "Erro ao aprovar residuo");
      });
  }

  iniciarRecusaResiduo(residuo: any) {
    this.residuoRecusando = residuo;
    this.motivoResiduo = null;
    this.comentarioResiduo = "";
  }

  cancelarRecusaResiduo() {
    this.residuoRecusando = null;
    this.motivoResiduo = null;
    this.comentarioResiduo = "";
  }

  confirmarRecusaResiduo() {
    if (!this.motivoResiduo) {
      return this.showAlert("Selecione um motivo de recusa");
    }

    if (this.motivoResiduo.requer_comentario && !this.comentarioResiduo.trim()) {
      return this.showAlert("Este motivo requer uma observacao obrigatoria");
    }

    var load = this.loadCtrl.create({ content: "Recusando residuo..." });
    load.present();

    var motivoCodigo = this.motivoResiduo ? this.motivoResiduo.codigo : "";

    this.provider
      .recusarResiduo(this.residuoRecusando.id, motivoCodigo, this.comentarioResiduo)
      .then((data) => {
        load.dismiss();
        this.showToast("Residuo recusado");
        this.residuoRecusando = null;
        this.lancamento = data.data;
        this.verificarFinalizacao();
      })
      .catch((err) => {
        load.dismiss();
        this.showAlert(err.msg || "Erro ao recusar residuo");
      });
  }

  verificarFinalizacao() {
    if (this.todosValidados()) {
      this.showToast("Todos os residuos foram validados. Validacao finalizada!");
      // Recarregar para obter o status final
      this.carregarDados();
    }
  }

  finalizarValidacao() {
    var c = this.getContadorValidados();
    var msg = "Finalizar validacao? ";
    if (c.pendentes > 0) {
      msg += c.pendentes + " residuo(s) pendente(s) serao recusados automaticamente.";
    }

    this.alertCtrl
      .create({
        title: "Finalizar Validacao",
        message: msg,
        buttons: [
          { text: "Cancelar", role: "cancel" },
          {
            text: "Finalizar",
            handler: () => {
              this.executarFinalizacao();
            },
          },
        ],
      })
      .present();
  }

  executarFinalizacao() {
    var load = this.loadCtrl.create({ content: "Finalizando validacao..." });
    load.present();

    this.provider
      .finalizarValidacao(this.lancamentoId)
      .then((data) => {
        load.dismiss();
        this.showToast("Validacao finalizada com sucesso!");
        this.navCtrl.pop();
      })
      .catch((err) => {
        load.dismiss();
        this.showAlert(err.msg || "Erro ao finalizar validacao");
      });
  }

  // =========================================
  // HELPERS
  // =========================================

  formatDate(dateStr: string): string {
    if (!dateStr) return "-";
    var date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR");
  }

  formatCurrency(value: number): string {
    if (!value) return "R$ 0,00";
    return "R$ " + value.toFixed(2).replace(".", ",");
  }

  showAlert(msg: string) {
    this.alertCtrl
      .create({ title: "Aviso", message: msg, buttons: ["OK"] })
      .present();
  }

  showToast(msg: string) {
    this.toastCtrl.create({ message: msg, duration: 3000 }).present();
  }
}



// WEBPACK FOOTER //
// ./src/pages/ecoenel-empresas/reciclagem-responder/reciclagem-responder.ts